/**
 * The register, handed over as a spreadsheet.
 *
 * ── THE LIE THIS FILE EXISTS TO AVOID ───────────────────────────────────────
 *
 * The screen holds one page. A button labelled "Export" that writes the 25 rows
 * in front of the operator, out of 80 that match the filter, is a silent lie in
 * a file that gets emailed to an accountant - silent because a spreadsheet with
 * 25 correct rows in it looks exactly like a complete one. So the scope is never
 * assumed here. Either the page IS the whole filtered set, in which case there
 * is nothing to choose and the button just exports, or it is not, and the
 * operator is asked which of the two they mean before anything is written.
 *
 * ── HOW "EVERYTHING THE FILTERS MATCH" IS ACTUALLY FETCHED ─────────────────
 *
 * By paging the same endpoint at its ceiling until the rows run out, not by
 * asking for a bigger page. The register's `pageSize` maxes at 100 (see
 * `projectListQuery`), and a single request at that ceiling would quietly stop
 * at 100 the first time a client filter matched 140 projects - which is the same
 * lie one size larger, and harder to notice because it only appears once the
 * register has grown.
 *
 * The loop is capped (`MAX_EXPORT_PAGES`). The cap is not a scope decision, it
 * is a stop: a browser walking a register of tens of thousands of rows one
 * hundred at a time is a tab that appears to hang, and the honest fix at that
 * size is a server-side export that streams one query. If the cap is ever hit
 * the file is short, and the toast is what makes that visible - it reports the
 * number of rows WRITTEN, which the operator can read against the "Showing
 * 1-25 of N" line the register prints two centimetres below. A receipt that
 * said "done" would hide exactly the case worth seeing.
 *
 * ── THE FILTERS ARE NOT REBUILT HERE ───────────────────────────────────────
 *
 * `params` arrives from the list, already exactly what `load()` sends. A second
 * copy of the filter assembly in this file is a copy that drifts, and the day it
 * drifts the export describes a different set of projects from the rows above
 * it, with nothing on screen to say so. Sorting comes along for the same ride,
 * so the file lands in the order the register is in.
 */
import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { csvName, downloadCsv } from "../../../lib/csv";
import { getProjects, type Project, type ProjectListParams } from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";
import { Btn, useToast } from "../../ui";
import { staffLabel, useStaff } from "../_shared/staff";
import { projectCsvRows } from "./projectsCsv";

/** The endpoint's own ceiling. Asking for more is refused as an invalid query,
 *  so this is the largest honest bite. */
const EXPORT_PAGE_SIZE = 100;

/** See the file header: a stop, not a scope. 4000 projects is far past any
 *  register this product has, and well short of a tab that stops responding. */
const MAX_EXPORT_PAGES = 40;

/** Everything the filters match, walked one page at a time. Returns what it
 *  managed to collect, which the caller reports rather than rounds up. */
async function fetchAll(params: ProjectListParams): Promise<Project[]> {
  const all: Project[] = [];
  for (let page = 1; page <= MAX_EXPORT_PAGES; page += 1) {
    const res = await getProjects({ ...params, page, pageSize: EXPORT_PAGE_SIZE });
    all.push(...res.rows);
    // Two exits, and both are needed. `all.length >= res.total` is the normal
    // one; the empty page is the guard for a set that shrank between requests -
    // somebody archiving a project mid-export would otherwise keep the loop
    // asking for a page that no longer exists until it hits the cap.
    if (res.rows.length === 0 || all.length >= res.total) break;
  }
  return all;
}

export function ProjectsExport({
  params,
  pageRows,
  total,
}: {
  /**
   * Exactly what the list fetched with. Hand it the register's whole parameter
   * object, paging included - `fetchAll` writes `page` and `pageSize` over
   * whatever is in here, so a caller never has to strip them and cannot strip
   * the wrong one. What matters is that this is the SAME object the rows came
   * from and not a second assembly of the same filters; see the file header.
   */
  params: ProjectListParams;
  /** The rows currently drawn, so "this page only" needs no request at all. */
  pageRows: readonly Project[];
  /** How many projects the filter matches in total, from the same response. */
  total: number;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  const { byId } = useStaff();
  const [busy, setBusy] = useState(false);

  // The whole answer is already on screen. Offering "this page" and "everything"
  // here would be a menu whose two entries write the identical file.
  const pageIsEverything = total <= pageRows.length;

  function write(rows: readonly Project[]) {
    downloadCsv(
      csvName("projects", new Date()),
      projectCsvRows(rows, { t, memberName: (id) => staffLabel(byId, id) }),
    );
    // The count, not a word. It is the only thing that makes a short file
    // visible - see the file header.
    toast(t("projects.exportedRows", { n: rows.length }));
  }

  async function writeAll() {
    if (busy) return;
    setBusy(true);
    try {
      write(await fetchAll(params));
    } catch {
      // Nothing is downloaded on a failure. A partial file that arrived anyway
      // would be indistinguishable from a complete one the moment it left the
      // browser.
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  if (pageIsEverything) {
    return (
      <Btn onClick={() => write(pageRows)} disabled={!pageRows.length}>
        <Download className="size-4" />
        {t("projects.export")}
      </Btn>
    );
  }

  return (
    <Pop
      trigger={(open) => (
        <Btn aria-haspopup="menu" aria-expanded={open} disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
          {t("projects.export")}
        </Btn>
      )}
    >
      {/* "Everything" first: it is the one an operator asking for "the projects"
          almost always means, and putting the narrower choice at the top is how
          a page-sized file gets picked by reflex. */}
      <button
        type="button"
        className="crm-mi"
        onClick={() => {
          close();
          void writeAll();
        }}
      >
        {t("projects.exportAll")}
      </button>
      <button
        type="button"
        className="crm-mi"
        onClick={() => {
          close();
          write(pageRows);
        }}
      >
        {t("projects.exportPage")}
      </button>
    </Pop>
  );
}
